/**
 * Amplitude Analytics Service
 * 
 * This service handles Amplitude analytics initialization and event tracking
 * for the Aris application.
 */

import * as amplitude from '@amplitude/analytics-browser';

// Amplitude API Key - should be set via environment variable in production
const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY || '';

class AmplitudeService {
  constructor() {
    this.isInitialized = false;
    this.userId = null;
  }

  /**
   * Initialize Amplitude with optional user ID
   * @param {string|null} userId - User ID to associate with events
   * @returns {boolean} - Whether initialization was successful
   */
  initialize(userId = null) {
    if (!AMPLITUDE_API_KEY) {
      console.warn('[Amplitude] API key not configured. Set REACT_APP_AMPLITUDE_API_KEY environment variable.');
      return false;
    }

    try {
      // Initialize Amplitude with configuration
      amplitude.init(AMPLITUDE_API_KEY, userId, {
        // Default configuration
        autocapture: true,
        // Flush events every 30 seconds or when 30 events are queued
        flushIntervalMillis: 30000,
        flushQueueSize: 30,
      });

      this.isInitialized = true;
      this.userId = userId;

      console.log('[Amplitude] Initialized successfully', userId ? `for user: ${userId}` : '(anonymous)');
      return true;
    } catch (error) {
      console.error('[Amplitude] Initialization failed:', error);
      return false;
    }
  }

  /**
   * Set or update the user ID
   * @param {string} userId - User ID to set
   */
  setUserId(userId) {
    if (!this.isInitialized) {
      console.warn('[Amplitude] Not initialized. Call initialize() first.');
      return;
    }

    if (userId) {
      amplitude.setUserId(userId);
      this.userId = userId;
      console.log('[Amplitude] User ID set:', userId);
    }
  }

  /**
   * Get user ID from localStorage profile
   * @returns {string|null} - User ID or null if not found
   */
  getUserIdFromStorage() {
    try {
      const profileRaw = localStorage.getItem('profile');
      if (profileRaw) {
        const profile = JSON.parse(profileRaw);
        return profile?._id || profile?.id || profile?.user_id || null;
      }
    } catch (error) {
      console.error('[Amplitude] Error reading user ID from storage:', error);
    }
    return null;
  }

  /**
   * Initialize Amplitude with user ID from localStorage
   * @returns {boolean} - Whether initialization was successful
   */
  initializeWithStoredUser() {
    const userId = this.getUserIdFromStorage();
    return this.initialize(userId);
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} eventProperties - Additional properties for the event
   */
  track(eventName, eventProperties = {}) {
    if (!this.isInitialized) {
      console.warn('[Amplitude] Not initialized. Event not tracked:', eventName);
      return;
    }

    try {
      amplitude.track(eventName, eventProperties);
      console.log('[Amplitude] Event tracked:', eventName, eventProperties);
    } catch (error) {
      console.error('[Amplitude] Error tracking event:', error);
    }
  }

  /**
   * Track page view event
   * @param {string} pageName - Name of the page
   * @param {Object} additionalProperties - Additional properties
   */
  trackPageView(pageName, additionalProperties = {}) {
    this.track('Page View', {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      ...additionalProperties,
    });
  }

  /**
   * Track authentication events
   * @param {string} action - Authentication action (login, signup, logout, etc.)
   * @param {Object} properties - Additional properties
   */
  trackAuth(action, properties = {}) {
    this.track(`Auth: ${action}`, {
      auth_action: action,
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties to set
   */
  setUserProperties(properties) {
    if (!this.isInitialized) {
      console.warn('[Amplitude] Not initialized. User properties not set.');
      return;
    }

    try {
      const identify = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value);
      });
      amplitude.identify(identify);
      console.log('[Amplitude] User properties set:', properties);
    } catch (error) {
      console.error('[Amplitude] Error setting user properties:', error);
    }
  }

  /**
   * Clear user data (for logout)
   */
  reset() {
    if (!this.isInitialized) {
      return;
    }

    try {
      amplitude.reset();
      this.userId = null;
      console.log('[Amplitude] User data reset');
    } catch (error) {
      console.error('[Amplitude] Error resetting:', error);
    }
  }

  /**
   * Check if Amplitude is initialized
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized;
  }
}

// Export singleton instance
const amplitudeService = new AmplitudeService();
export default amplitudeService;

// Export individual functions for convenience
export const initAmplitude = (userId) => amplitudeService.initialize(userId);
export const initAmplitudeWithStoredUser = () => amplitudeService.initializeWithStoredUser();
export const setAmplitudeUserId = (userId) => amplitudeService.setUserId(userId);
export const trackEvent = (eventName, properties) => amplitudeService.track(eventName, properties);
export const trackPageView = (pageName, properties) => amplitudeService.trackPageView(pageName, properties);
export const trackAuth = (action, properties) => amplitudeService.trackAuth(action, properties);
export const setUserProperties = (properties) => amplitudeService.setUserProperties(properties);
export const resetAmplitude = () => amplitudeService.reset();
